import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  keyCompositionService,
  type KeyCompositionCreateRequest,
  type KeyCompositionListFilters,
  type KeyCompositionResolveParams,
  type KeyCompositionSupersedeRequest,
} from '../../services/api/keyCompositionService';

/**
 * Cache key used by `keyCompositionSlice.resolved` so duplicate consumers for
 * the same (tenant, keyType, mixKind, buyer, supplier, channel, po) tuple
 * share state.
 */
export function keyCompositionCacheKey(p: KeyCompositionResolveParams): string {
  return [
    p.tenantId,
    p.keyType,
    p.mixKind,
    p.buyerPartyId ?? '_',
    p.supplierPartyId ?? '_',
    p.channelCode ?? '_',
    p.poNumber ?? '_',
    p.asOfUtc ?? '_',
  ].join('|');
}

export const fetchKeyCompositionList = createAsyncThunk(
  'keyComposition/fetchList',
  async (filters: KeyCompositionListFilters, { rejectWithValue }) => {
    try {
      return await keyCompositionService.list(filters);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch key-composition rows');
    }
  }
);

export const resolveKeyComposition = createAsyncThunk(
  'keyComposition/resolve',
  async (params: KeyCompositionResolveParams, { rejectWithValue }) => {
    try {
      const data = await keyCompositionService.resolve(params);
      return { key: keyCompositionCacheKey(params), data };
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to resolve key-composition profile');
    }
  }
);

export const createKeyComposition = createAsyncThunk(
  'keyComposition/create',
  async (
    { tenantId, request }: { tenantId: number; request: KeyCompositionCreateRequest },
    { rejectWithValue }
  ) => {
    try {
      return await keyCompositionService.create(tenantId, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create key-composition profile');
    }
  }
);

export const supersedeKeyComposition = createAsyncThunk(
  'keyComposition/supersede',
  async (
    { tenantId, id, request }: { tenantId: number; id: number; request: KeyCompositionSupersedeRequest },
    { rejectWithValue }
  ) => {
    try {
      return await keyCompositionService.supersede(tenantId, id, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to supersede key-composition profile');
    }
  }
);

export const deleteKeyComposition = createAsyncThunk(
  'keyComposition/delete',
  async (
    { tenantId, id }: { tenantId: number; id: number },
    { rejectWithValue }
  ) => {
    try {
      await keyCompositionService.remove(tenantId, id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete key-composition profile');
    }
  }
);
